import * as React from 'react';
import styled from 'styled-components';
import { UseFormRegisterReturn } from 'react-hook-form';
import { conformToMask } from 'text-mask-core';
import {
  useGlobalSettings,
  withGlobalSettingsProvider,
} from '@narvar/nth-hook-global-settings';
import { usePageContext } from '@narvar/nth-hook-context-page';
import { SmsCountry } from '@narvar/nth-hook-loma';
import { useTheme, withThemeProvider } from '@narvar/nth-hook-theme';
import Chevron, { Direction } from '@narvar/nth-bit-icon-chevron';
import flags, { mappedFlags, defaultMask } from './lib/flags';
import { isSmsCountry } from './lib/smsCountry';
import CountrySelector from './components/CountrySelector';
import BaseInput, { SupportedInputType } from '../BaseInput';
import ErrorMessage from '../ErrorMessage';

/**
 * NOTE: This is pretty much just `nth-block-telephone-input` copied over.
 *       The differences are as follows:
 *       1) Modified the value state management. `nth-block-telephone-input` keeps
 *          the value in local state. To make integration with react-hook-form easier,
 *          as with other inputs, we want react-hook-form to manage that state
 *       2) Converted the `useEffect` hook which formatted the phone number value after the
 *          local state value changed into a `formatNumber` function, and is now
 *          called in the input's onChange event, before we call react-hook-form's
 *          onChange handler.
 *       3) Brought the error message within the component. The eventual goal would be to have
 *          a set of input components which have error handling baked into the components. This
 *          is a step toward that goal.
 */

const domesticCountries: SmsCountry[] = ['US', 'CA'];

interface InputContainerProps {
  sDefault?: React.CSSProperties;
  sError?: React.CSSProperties;
}

const InputContainer = styled.div<InputContainerProps>(
  ({ sDefault, sError }) => ({
    boxSizing: 'border-box',
    width: '100%',
    margin: '0 auto',
    border: '1px solid #999',
    borderRadius: '0.125rem',
    '&:focus': {
      borderColor: '#999',
      color: '#000',
      ...sError,
    },
    ...sDefault,
    ...sError,
  }),
);

interface TelephoneInputProps {
  id: string;
  errorMessage?: string;
  value?: string;
  register: UseFormRegisterReturn;
  onChangeCallback?: (_: string) => void;
  label: string;
  centered?: boolean;
  animateLabel?: boolean;
  featureName?: string;
  s?: React.CSSProperties;
}

const TelephoneInput: React.FunctionComponent<TelephoneInputProps> = ({
  label,
  centered = false,
  animateLabel = false,
  featureName,
  register,
  onChangeCallback,
  value,
  id,
  errorMessage,
  s = {},
}) => {
  const { smsDefaultCountry } = useGlobalSettings();

  const {
    state: { localeCountry },
  } = usePageContext();

  const defaultCountry: SmsCountry =
    smsDefaultCountry || (isSmsCountry(localeCountry) ? localeCountry : 'US');
  const international = !domesticCountries.includes(defaultCountry);

  const [selectedCountry, setSelectedCountry] = React.useState(
    mappedFlags[defaultCountry],
  );

  const {
    telephoneinputNoneNone: telephoneInputStyle,
    captiontextErrorNone: captionTextError,
  } = useTheme();

  const formatNumber = (inputValue: string): string => {
    const { mask = defaultMask } = selectedCountry;
    const phoneNumber = inputValue.replace(/\D/g, '');
    const { conformedValue } = conformToMask(
      phoneNumber.length > 10 ? phoneNumber.substr(0, 10) : phoneNumber,
      mask,
      {
        guide: false,
        previousConformedValue: phoneNumber,
      },
    );

    return international || !mask.length ? phoneNumber : conformedValue;
  };

  React.useEffect(() => {
    if (!defaultCountry) return;
    setSelectedCountry(mappedFlags[defaultCountry]);
  }, [defaultCountry]);

  const { onBlur: rOnBlur, onChange: rOnChange, ref, name } = register;
  const errorId = `${id}-error`;
  const hasError = Boolean(errorMessage && errorMessage.length);
  const errorColor = (captionTextError as { color?: string } | undefined)
    ?.color;

  const onBlur = React.useCallback(
    (event?: React.FocusEvent<HTMLInputElement>) => {
      if (event) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        rOnBlur(event);
      }
    },
    [rOnBlur],
  );

  return (
    <>
      <InputContainer
        sDefault={telephoneInputStyle}
        sError={
          hasError && errorColor ? { border: `1px solid ${errorColor}` } : {}
        }
      >
        <div
          style={{
            display: 'table-cell',
            verticalAlign: 'middle',
            position: 'relative',
            fontSize: '1.5rem',
          }}
        >
          {international && (
            <div data-test="telephoneInputLanguageDropdownContainer">
              <div style={{ display: 'table', pointerEvents: 'none' }}>
                {selectedCountry && (
                  <div
                    style={{
                      display: 'table-cell',
                      marginRight: '0.5rem',
                      paddingLeft: '0.5rem',
                    }}
                    id={selectedCountry.name}
                  >
                    {selectedCountry.emoji}
                  </div>
                )}
                <div
                  style={{
                    display: 'table-cell',
                    paddingLeft: '0.5rem',
                    verticalAlign: 'top',
                  }}
                >
                  <Chevron
                    s={{ width: '0.75rem', height: '0.75rem' }}
                    direction={Direction.Down}
                  />
                </div>
              </div>
              <CountrySelector
                selectedCountryCode={
                  (selectedCountry && selectedCountry.code) || null
                }
                minHeight={(s && s.height) || '2.375rem'}
                onSelectCallback={(
                  event: React.ChangeEvent<HTMLSelectElement>,
                ): void =>
                  setSelectedCountry(flags[Number(event.target.value)])
                }
              />
            </div>
          )}
        </div>
        <div
          data-test="telephoneInputNumberContainer"
          style={
            international
              ? {
                  display: 'table-cell',
                  width: '100%',
                }
              : {}
          }
        >
          <BaseInput
            value={value}
            ref={ref}
            id={id}
            type={SupportedInputType.tel}
            name={name}
            ariaDescribedby={errorId}
            onBlur={onBlur}
            onChange={(e) => {
              e.target.value = formatNumber(e.target.value);
              // eslint-disable-next-line @typescript-eslint/no-floating-promises
              rOnChange(e);

              if (onChangeCallback) {
                onChangeCallback(
                  `${international ? selectedCountry.dialCode : ''}${
                    e.target.value
                  }`,
                );
              }
            }}
            centered={centered}
            animateLabel={animateLabel}
            label={label}
            featureName={featureName}
            containerOverridesStyle={{ border: 'none' }}
          />
        </div>
      </InputContainer>
      <ErrorMessage id={errorId} featureName={featureName} show={hasError}>
        {errorMessage}
      </ErrorMessage>
    </>
  );
};

export default withGlobalSettingsProvider(
  withThemeProvider(TelephoneInput, {
    themeTokens: ['telephoneinputNoneNone', 'captiontextErrorNone'],
  }),
  { globalSettingsRequested: 'smsDefaultCountry' },
);
